import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaClock,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain,
  FaPlaceOfWorship
} from 'react-icons/fa';

import './KedarnathBadrinath.css';

const KedarnathBadrinath = () => {

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: 'smooth'
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Haridwar | Ganga Aarti Experience",
      points: [
        "Arrival at Haridwar Railway Station or Dehradun Airport and transfer to hotel.",
        "Attend the sacred Ganga Aarti at Har Ki Pauri.",
        "Experience the spiritual atmosphere of Haridwar.",
        "Overnight stay in Haridwar."
      ]
    },

    {
      day: 2,
      title: "Haridwar to Guptkashi via Devprayag & Dhari Devi",
      points: [
        "Drive towards Guptkashi through scenic Himalayan roads.",
        "Visit Devprayag – sacred confluence of Alaknanda and Bhagirathi rivers.",
        "Visit Dhari Devi Temple near Srinagar.",
        "Stop at Rudraprayag to witness river confluence.",
        "Visit Vishwanath Temple in Guptkashi.",
        "Overnight stay in Guptkashi."
      ]
    },

    {
      day: 3,
      title: "Guptkashi to Kedarnath | Trek to Jyotirlinga Shrine",
      points: [
        "Drive to Sonprayag and transfer to Gaurikund.",
        "Visit Gaurikund Temple dedicated to Goddess Parvati.",
        "Begin Kedarnath trek (16–18 km) or optional pony/palki/helicopter services.",
        "Proceed for Kedarnath Temple darshan.",
        "Overnight stay in Kedarnath."
      ]
    },

    {
      day: 4,
      title: "Kedarnath to Guptkashi",
      points: [
        "Early morning darshan and rituals at Kedarnath Temple.",
        "Descend back to Gaurikund.",
        "Drive back to Guptkashi.",
        "Rest and overnight stay in Guptkashi."
      ]
    },

    {
      day: 5,
      title: "Guptkashi to Badrinath via Karnaprayag & Joshimath",
      points: [
        "Drive through scenic Alaknanda valley.",
        "Visit Karnaprayag and Nandaprayag.",
        "Pass through Joshimath, winter seat of Lord Badrinath.",
        "Evening darshan at Badrinath Temple.",
        "Overnight stay in Badrinath."
      ]
    },

    {
      day: 6,
      title: "Badrinath – Mana Village – Rudraprayag",
      points: [
        "Take holy dip at Tapt Kund.",
        "Visit Mana Village – last Indian village near Tibet border.",
        "Explore Bheem Pul associated with Mahabharata legends.",
        "Drive towards Rudraprayag.",
        "Overnight stay in Rudraprayag."
      ]
    },

    {
      day: 7,
      title: "Rudraprayag to Haridwar via Rishikesh",
      points: [
        "Drive along scenic Ganga valley.",
        "Visit Ram Jhula and Lakshman Jhula in Rishikesh.",
        "Continue journey to Haridwar.",
        "Overnight stay in Haridwar."
      ]
    },

    {
      day: 8,
      title: "Departure",
      points: [
        "After breakfast check out from hotel.",
        "Transfer to Haridwar Railway Station or Dehradun Airport.",
        "Tour concludes with divine blessings and unforgettable memories."
      ]
    }
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}

      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >

        <div className="header-left">

          <h1 className="trip-title">
            Divine 2 Dham Kedarnath & Badrinath Yatra
          </h1>

          <p className="trip-duration">
            7 Nights / 8 Days | Start & End: Haridwar
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>
              <p>Haridwar, Kedarnath, Badrinath, Rishikesh</p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Service Type:</span>
              <p>Spiritual Pilgrimage Journey</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Himalayan Temple Yatra</p>
          </div>

        </div>

      </motion.header>

      {/* IMAGE GRID */}

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=900&auto=format&fit=crop"
            alt="Kedarnath"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://images.unsplash.com/photo-1622308644420-b20142dc993c?q=80&w=900&auto=format&fit=crop"
            alt="Badrinath"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1595815771614-ade9d652a65d?q=80&w=900&auto=format&fit=crop"
              alt="Rishikesh"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1589793907316-f94025b46850?q=80&w=900&auto=format&fit=crop"
              alt="Haridwar"
            />
          </div>

        </div>

      </motion.div>

      {/* STICKY NAV */}

      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>
          Detailed Itinerary
        </button>

        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>
          Highlights
        </button>

        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>
          Policy & Inclusions
        </button>

        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>
          About Trip
        </button>
      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}

        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Day Wise Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (

              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">
                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>
                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (

                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>

                  ))}

                </div>

              </motion.div>

            ))}

          </div>

        </section>

        {/* HIGHLIGHTS */}

        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Darshan at Kedarnath Temple Jyotirlinga</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Darshan at sacred Badrinath Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Devprayag, Rudraprayag & Karnaprayag</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Holy dip at Tapt Kund</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Mana Village – Last village of India</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Evening Ganga Aarti at Har Ki Pauri</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Scenic Himalayan drives and river valleys</p>
            </div>

          </div>

        </motion.section>

        {/* INCLUSIONS */}

        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle /> What's Included
            </h4>

            <ul className="p-list">

              <li>
                <FaHotel /> Accommodation for 7 nights
              </li>

              <li>
                <FaUtensils /> Breakfast and dinner at hotels
              </li>

              <li>
                <FaBus /> All transportation and transfers
              </li>

              <li>
                <FaMountain /> Sightseeing as per itinerary
              </li>

              <li>
                <FaCheckCircle /> Driver charges, fuel, toll & parking
              </li>

              <li>
                <FaShieldAlt /> Tour coordination and assistance
              </li>

            </ul>

          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle /> What's Excluded
            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle /> Helicopter / pony / palki charges
              </li>

              <li>
                <FaTimesCircle /> Temple entry fees if applicable
              </li>

              <li>
                <FaTimesCircle /> Personal expenses and shopping
              </li>

              <li>
                <FaTimesCircle /> Travel insurance
              </li>

              <li>
                <FaTimesCircle /> Medical or emergency expenses
              </li>

              <li>
                <FaTimesCircle /> Additional sightseeing not mentioned
              </li>

            </ul>

          </div>

        </motion.section>

        {/* ABOUT */}

        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">

            The Divine Kedarnath & Badrinath Yatra is a spiritually significant
            pilgrimage through the sacred Himalayan shrines of Uttarakhand.
            This journey combines devotion, scenic mountain landscapes, sacred
            river confluences, and ancient temples, creating a deeply peaceful
            and memorable experience.

            <br /><br />

            From the divine Kedarnath Jyotirlinga to the sacred Badrinath Temple,
            travelers experience the spiritual heart of the Himalayas while also
            exploring places like Devprayag, Mana Village, Rishikesh, and Haridwar.

          </p>

        </motion.section>

        {/* TERMS */}

        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* PRIVACY */}

        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: 'none',
              color: 'inherit'
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: 'rotate(-90deg)' }}
            />

          </Link>

        </div>

        {/* NOTE */}

        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section travel-info-grid"
        >

          <div className="info-card note-card" style={{ width: '100%' }}>

            <h4 className="info-card-title">
              <FaPaperPlane /> Important Note
            </h4>

            <p>
              Travel timings mentioned in the itinerary are approximate and may
              vary depending on mountain road conditions, weather, traffic,
              and temple crowd levels.
            </p>

          </div>

        </motion.section>

      </div>

    </div>
  );
};

export default KedarnathBadrinath;